import { Link as RouterLink } from 'react-router';
import { Box, Grid, Link, Paper, useMediaQuery, useTheme } from '@mui/material';
import { ArticlesSection } from '../components/Article/ArticlesSection';
import { useTranslation } from 'react-i18next';
import ArticlesSectionSkeleton from '../components/Article/ArticlesSectionSkeleton';
import ErrorView from '../components/ErrorViews/ErrorView';
import useDocumentTitle from '../hooks/useDocumentTitle';
import PageHeader from '../components/Page/PageHeader';
import { useMemo } from 'react';
import OpenGraphGeneral from '../components/MetaData/OpenGraphGeneral';
import { apiStates } from '../types/types';
import SuggestedCategoriesList from '../components/Page/SuggestedCategoriesList';
import { articleCardLocation } from '../utilities/gtm';
import EditorialRecommendations from '../components/Article/EditorialRecommendations';
import { UseArticlesResult, useArticles } from '../hooks/useArticles';
import { useAppSelector } from '../store/hooks';
import {
  categoryStateEnum,
  selectCategories,
  selectState,
} from '../store/categories';
import { getPrimaryCategories } from '../utilities/Categories';
import CategoryFiltersDrawer from '../components/Category/CategoryFilters/Drawer/CategoryFiltersDrawer';
import { JaaRoute } from '../utilities/Links';
import CategoryFiltersList from '../components/Category/CategoryFilters/List/CategoryFiltersList';
import NoResultsText from '../components/Article/NoResultsText';
import useSuggestedCategories from '../hooks/useSuggestedCategories';
import useContentSearchParams from '../hooks/useContentSearchParams';
import { Helmet } from 'react-helmet-async';
import { ArticleCardVariant } from '../components/ArticleCard/CardUtils';

export default function ContentIndexPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const showCategories = useMediaQuery(theme.breakpoints.up('md'));
  const addHiddenGridColumn = useMediaQuery(
    theme.breakpoints.between('md', 'lg'),
  );
  const showHighlightedMinimizedCard = useMediaQuery(
    theme.breakpoints.up('lg'),
  );

  const showMobileCard = useMediaQuery(theme.breakpoints.down('md'));

  let editorialRecommendationCardVariant: ArticleCardVariant = 'default';

  if (showHighlightedMinimizedCard) {
    editorialRecommendationCardVariant = 'minimizedHighlight';
  } else if (showMobileCard) {
    editorialRecommendationCardVariant = 'mobile';
  }

  // Handle active category and content type
  const {
    category: activeCategory,
    contentType,
    updateCategory,
  } = useContentSearchParams();

  const pageTitle = useMemo(() => {
    const contentTypeTitle = contentType
      ? t('contentType.' + contentType, {
          count: 2,
        })
      : t('contentType.all');

    return `${contentTypeTitle}${activeCategory ? ': ' : ''}${activeCategory ?? ''}`;
  }, [t, contentType, activeCategory]);

  useDocumentTitle(pageTitle);

  // Handle categories
  const loadedCategories = useAppSelector(selectCategories);
  const categoryState = useAppSelector(selectState);

  const sortedPrimaryCategories: string[] = useMemo(() => {
    return [...getPrimaryCategories(contentType)].sort((a, b) =>
      a.localeCompare(b),
    );
  }, [contentType]);

  const secondaryCategories: string[] = useMemo(() => {
    return loadedCategories
      .map((category) => {
        return category.name;
      })
      .filter((loadedCategory) => {
        return !sortedPrimaryCategories.includes(loadedCategory);
      })
      .sort((a, b) => a.localeCompare(b));
  }, [loadedCategories, sortedPrimaryCategories]);

  const categories = useMemo(() => {
    return [...sortedPrimaryCategories, ...secondaryCategories];
  }, [sortedPrimaryCategories, secondaryCategories]);

  const suggestedCategories = useSuggestedCategories(activeCategory);

  // Handle fetching articles
  const {
    hasNextPage,
    fetchNextPage,
    articles,
    apiError,
    state: apiState,
    totalArticleCount,
  }: UseArticlesResult = useArticles({
    limit: 10,
    category: activeCategory,
    contentType,
  });

  // If category is not matching any known categories, show "Content not found" view
  if (apiState === apiStates.HAS_ERROR) {
    return (
      <Box
        sx={{
          paddingX: { xs: 2, sm: 0 },
        }}
      >
        <ErrorView apiError={apiError} />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <link
          rel='canonical'
          href='https://www.juridiikka.almainsights.fi/sisallot'
        />
      </Helmet>

      <PageHeader
        title={pageTitle}
        titleItem={
          showCategories ? undefined : (
            <CategoryFiltersDrawer
              activeCategory={activeCategory}
              categories={categories}
              loading={categoryState === categoryStateEnum.LOADING}
              onSelectCategory={updateCategory}
            />
          )
        }
      >
        {suggestedCategories.length > 0 && (
          <Box
            sx={{
              marginTop: 3,
            }}
          >
            <SuggestedCategoriesList
              categories={suggestedCategories}
              onSelectCategory={updateCategory}
            />
          </Box>
        )}
      </PageHeader>
      <Grid container columnSpacing={3}>
        <Grid component='aside' size={{ xs: 12, md: 3 }}>
          {showCategories && (
            <>
              <Box
                sx={{
                  paddingBottom: 3,
                }}
              >
                <CategoryFiltersList
                  activeCategory={activeCategory}
                  categories={sortedPrimaryCategories}
                  headingText={t('categoryList.primaryCategories.title')}
                  onSelectCategory={updateCategory}
                />
              </Box>
              <CategoryFiltersList
                activeCategory={activeCategory}
                categories={secondaryCategories}
                hideIndex
                headingText={t('categoryList.secondaryCategories.title')}
                loading={categoryState === categoryStateEnum.LOADING}
                onSelectCategory={updateCategory}
              />
            </>
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 9, lg: 7 }}>
          <Box
            sx={{
              marginX: { xs: -2, sm: 0 },
            }}
          >
            {apiState === apiStates.FETCHING && (
              <ArticlesSectionSkeleton hideCoverImages />
            )}
            {apiState === apiStates.READY && (
              <ArticlesSection
                key={activeCategory}
                headingLevel='h2'
                articles={articles}
                hasNextPage={hasNextPage}
                fetchNextPage={fetchNextPage}
                totalArticleCount={totalArticleCount}
                showArticleAmount
                hideCoverImages
                location={articleCardLocation.MAIN_CONTENT}
                emptyView={
                  activeCategory ? (
                    <Paper
                      variant='outlined'
                      sx={{ paddingX: 2, paddingY: 1.5, marginY: 1 }}
                    >
                      <NoResultsText
                        contentType={contentType}
                        TypographyProps={{ variant: 'h3', paddingY: 1 }}
                      />
                      <Link
                        component={RouterLink}
                        to={`${JaaRoute.contentIndex}?category=${encodeURIComponent(activeCategory)}`}
                        underline='hover'
                        variant='body1'
                        sx={{
                          paddingY: 1,
                          display: 'inline-flex',
                        }}
                      >
                        Katso muita sisältöjä aiheesta {activeCategory}.
                      </Link>
                    </Paper>
                  ) : undefined
                }
              />
            )}
          </Box>
        </Grid>
        {addHiddenGridColumn && (
          <Grid
            size={{
              xs: 0,
              md: 3,
              lg: 0,
            }}
          />
        )}
        <Grid
          component='aside'
          size={{
            xs: 12,
            md: 9,
            lg: 2,
          }}
        >
          <Box component='section'>
            <EditorialRecommendations
              category={activeCategory}
              contentType={contentType}
              variant={editorialRecommendationCardVariant}
            />
          </Box>
        </Grid>
      </Grid>
      <OpenGraphGeneral />
    </>
  );
}
